import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';

/**
 * Preprocess LaTeX to fix common AI-generated issues
 */
function preprocessLatex(latex) {
  if (!latex) return latex;
  
  return latex
    // Remove LaTeX spacing commands (\, \: \; \! \quad \qquad)
    .replace(/\\[,;:!]/g, ' ')
    .replace(/\\(quad|qquad)\b/g, '  ')
    // Fix control characters that appear from JSON parsing of LaTeX commands:
    // \frac -> \f (form-feed 0x0C) + rac{
    .replace(/\x0crac\{/g, '\\frac{')
    .replace(/\x0corall\b/g, '\\forall')
    // \text -> \t (tab 0x09) + ext{  — CRITICAL: fixes "extCH2" bug
    .replace(/\x09ext\b/g, '\\text')
    .replace(/\x09extrm\b/g, '\\textrm')
    .replace(/\x09extbf\b/g, '\\textbf')
    .replace(/\x09extit\b/g, '\\textit')
    // \theta -> \t (tab 0x09) + heta
    .replace(/\x09heta\b/g, '\\theta')
    .replace(/\x09au\b/g, '\\tau')
    .replace(/\x09imes\b/g, '\\times')
    .replace(/\x09ilde/g, '\\tilde')
    .replace(/\x09riangle\b/g, '\\triangle')
    .replace(/\x09ag\b/g, '\\tag')
    .replace(/\x09op\b/g, '\\top')
    .replace(/\x09o\b/g, '\\to')
    // \beta -> \b (backspace 0x08) + eta
    .replace(/\x08eta\b/g, '\\beta')
    .replace(/\x08ar\b/g, '\\bar')
    .replace(/\x08ig\b/g, '\\big')
    // \nabla -> \n (newline 0x0A) + abla
    .replace(/\x0aabla\b/g, '\\nabla')
    .replace(/\x0aeq\b/g, '\\neq')
    .replace(/\x0aot\b/g, '\\not')
    .replace(/\x0au\b/g, '\\nu')
    .replace(/\x0ai\b/g, '\\ni')
    .replace(/\x0aeg\b/g, '\\neg')
    .replace(/\x0aotin\b/g, '\\notin')
    // \rho -> \r (carriage-return 0x0D) + ho
    .replace(/\x0dho\b/g, '\\rho')
    .replace(/\x0dight\b/g, '\\right')
    .replace(/\x0digharrow\b/g, '\\rightarrow')
    // Also handle rac{ at the very start of a string (form feed was stripped/invisible)
    .replace(/^rac\{/g, '\\frac{')
    .replace(/([^\\a-zA-Z])rac\{/g, '$1\\frac{')
    // Fix multiple escaping issues (from JSON stringification)
    .replace(/\\\\\\\\([a-zA-Z]+)/g, '\\$1')  // \\\\alpha -> \alpha
    .replace(/\\\\([a-zA-Z]+)/g, '\\$1')       // \\alpha -> \alpha (but preserve single backslash)
    // Fix common AI mistakes with \text{rac} instead of \frac
    .replace(/\\text\{rac\}/g, '\\frac')
    .replace(/\\text\{frac\}/g, '\\frac')
    .replace(/\\text\{sqrt\}/g, '\\sqrt')
    // Fix \text{} wrapped commands
    .replace(/\\text\{(\w+)\}\{/g, '\\$1{')
    // Fix missing backslash before common commands
    .replace(/([^\\])frac\{/g, '$1\\frac{')
    .replace(/([^\\])sqrt\{/g, '$1\\sqrt{')
    .replace(/([^\\])times(?![a-z])/g, '$1\\times')
    .replace(/([^\\])div(?![a-z])/g, '$1\\div')
    .replace(/([^\\])alpha(?![a-z])/g, '$1\\alpha')
    .replace(/([^\\])beta(?![a-z])/g, '$1\\beta')
    .replace(/([^\\])gamma(?![a-z])/g, '$1\\gamma')
    .replace(/([^\\])delta(?![a-z])/g, '$1\\delta')
    .replace(/([^\\])epsilon(?![a-z])/g, '$1\\epsilon')
    .replace(/([^\\])theta(?![a-z])/g, '$1\\theta')
    .replace(/([^\\])lambda(?![a-z])/g, '$1\\lambda')
    .replace(/([^\\])mu(?![a-z])/g, '$1\\mu')
    .replace(/([^\\])pi(?![a-z])/g, '$1\\pi')
    .replace(/([^\\])sigma(?![a-z])/g, '$1\\sigma')
    .replace(/([^\\])omega(?![a-z])/g, '$1\\omega')
    .replace(/([^\\])Omega(?![a-z])/g, '$1\\Omega')
    .replace(/([^\\])rho(?![a-z])/g, '$1\\rho')
    .replace(/([^\\])propto(?![a-z])/g, '$1\\propto')
    // Clean up extra spaces around braces
    .replace(/\{\s+/g, '{')
    .replace(/\s+\}/g, '}')
    // Fix 0 in subscripts appearing as ε₀
    .replace(/\\varepsilon_0/g, '\\varepsilon_0')
    .replace(/ε_0/g, '\\varepsilon_0');
}

// Detect if a plain text segment looks like a bare LaTeX math expression
// (no $...$ delimiters but contains LaTeX commands)
const BARE_LATEX_PATTERN = /\\(frac|text|textrm|textbf|epsilon|varepsilon|phi|theta|alpha|beta|gamma|delta|lambda|mu|sigma|omega|pi|times|div|sqrt|int|sum|prod|rightarrow|leftarrow|leq|geq|neq|approx|infty|partial|nabla|cdot|pm|mp|circ)\b|\x0c[a-zA-Z]|\x09[a-zA-Z]/;

function tryRenderBareMath(text) {
  // If the text has no $ delimiters but appears to be a LaTeX expression,
  // wrap it and try to render as inline math
  if (!text.includes('$') && BARE_LATEX_PATTERN.test(text)) {
    const cleaned = preprocessLatex(text.trim());
    return cleaned;
  }
  return null;
}

/**
 * Filter out [GRAPH: ...] and [DIAGRAM: ...] placeholder lines from note content
 * These are generated by AI when graph generation fails
 */
export function filterGraphPlaceholders(text) {
  if (!text) return text;
  return text
    .replace(/\[GRAPH:[^\]]*\]/g, '')
    .replace(/\[DIAGRAM:[^\]]*\]/g, '')
    .replace(/\n➤\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * Renders text with inline LaTeX math expressions
 * Supports both $...$ and \(...\) notation for inline math
 * And $$...$$ or \[...\] for block math
 * Also auto-detects bare LaTeX expressions without delimiters
 * Code blocks (backticks) are ALWAYS rendered as code, never as math.
 */
export default function MathText({ text, className = "" }) {
  if (!text) return null;
  
  // Function to render a single segment
  const renderSegment = (segment, index) => {
    // Triple-backtick fenced code block (```lang\ncode\n```)
    if (segment.startsWith('```')) {
      const match = segment.match(/^```(\w*)\n?([\s\S]*?)```$/);
      if (match) {
        const code = match[2].trimEnd();
        return (
          <pre key={index} className="bg-slate-800 text-slate-100 rounded-lg px-4 py-3 my-2 overflow-x-auto text-sm font-mono whitespace-pre leading-relaxed border border-slate-700">
            <code>{code}</code>
          </pre>
        );
      }
      // Fallback: render raw
      return <pre key={index} className="bg-slate-800 text-slate-100 rounded-lg px-4 py-3 my-2 overflow-x-auto text-sm font-mono whitespace-pre">{segment.replace(/^```\w*\n?/, '').replace(/```$/, '')}</pre>;
    }

    // Check for block math first ($$...$$)
    if (segment.startsWith('$$') && segment.endsWith('$$')) {
      const math = preprocessLatex(segment.slice(2, -2).trim());
      try {
        return <BlockMath key={index} math={math} />;
      } catch (e) {
        return <span key={index} className="text-red-500 bg-red-50 px-1 rounded">{segment}</span>;
      }
    }
    
    // Check for inline math ($...$)
    if (segment.startsWith('$') && segment.endsWith('$') && segment.length > 2) {
      const math = preprocessLatex(segment.slice(1, -1).trim());
      try {
        return <InlineMath key={index} math={math} />;
      } catch (e) {
        // Fallback: try to display as plain text
        return <span key={index} className="font-mono text-sm bg-gray-100 px-1 rounded">{math}</span>;
      }
    }
    
    // Single-backtick inline code — ALWAYS render as code, never as math.
    // Backtick notation is Markdown code convention; math must use $...$.
    if (segment.startsWith('`') && segment.endsWith('`') && segment.length > 2) {
      const code = segment.slice(1, -1);
      return <code key={index} className="bg-slate-700 text-emerald-300 px-2 py-0.5 rounded text-[0.85em] font-mono">{code}</code>;
    }
    
    // Check for bare LaTeX in plain text segment
    const bareMath = tryRenderBareMath(segment);
    if (bareMath !== null) {
      try {
        return <InlineMath key={index} math={bareMath} />;
      } catch (e) {
        // Fallback to preprocessed text
        return <span key={index} className="font-mono text-sm">{bareMath}</span>;
      }
    }
    
    return <span key={index}>{segment}</span>;
  };
  
  // Normalize literal \n and \t sequences (LLM stores these as two chars: backslash+n)
  // e.g. "uniform.\\nReason" → "uniform.\nReason" (actual newline for rendering)
  // CRITICAL: Only normalize OUTSIDE of $...$ math delimiters.
  // Inside math, \t starts \text/\theta/\tau, \n starts \nabla/\neq/\nu etc.
  const mathSplitPattern = /(\$\$[\s\S]*?\$\$|\$[^$]*?\$)/g;
  const normalizedText = text.split(mathSplitPattern).map((segment, i) => {
    // Odd indices are captured math groups — leave them untouched
    if (i % 2 === 1) return segment;
    // Even indices are non-math text — safe to normalize escape sequences
    return segment.replace(/\\n/g, '\n').replace(/\\t/g, '\t');
  }).join('');

  // Pre-process: convert \[...\] to $$...$$ and \(...\) to $...$
  const processedText = normalizedText
    .replace(/\\\[([\s\S]*?)\\\]/g, (_, m) => `$$${m.trim()}$$`)
    .replace(/\\\(([\s\S]*?)\\\)/g, (_, m) => `$${m.trim()}$`);

  // Split text by delimiters while keeping the matched segment.
  // Order matters: triple-backtick MUST appear before single-backtick so that
  // ```code``` is captured as a unit and not partially matched by `...`.
  const pattern = /(```[\s\S]*?```|\$\$[\s\S]*?\$\$|\$[^$\n]+?\$|`[^`\n]+?`)/g;
  const parts = processedText.split(pattern).filter(Boolean);

  // Auto-apply whitespace-pre-wrap when text has newlines (assertion-reasoning, multi-line questions)
  const hasNewlines = normalizedText.includes('\n');
  const finalClass = hasNewlines
    ? `${className} whitespace-pre-wrap`.trim()
    : className;

  return (
    <span className={finalClass}>
      {parts.map((part, index) => renderSegment(part, index))}
    </span>
  );
}

/**
 * Simple function to clean up math notation in text
 * Converts common patterns to cleaner display
 */
export function cleanMathText(text) {
  if (!text) return text;
  
  return text
    // Convert sqrt() to √
    .replace(/sqrt\(([^)]+)\)/g, '√($1)')
    // Convert ^2 to ²
    .replace(/\^2(?![0-9])/g, '²')
    // Convert ^3 to ³
    .replace(/\^3(?![0-9])/g, '³')
    // Convert * to ×
    .replace(/\s*\*\s*/g, ' × ')
    // Convert / to ÷ for simple fractions
    .replace(/(\d+)\s*\/\s*(\d+)/g, '$1÷$2');
}
